/**
 * useSemanticZoom — Distanzabhängige Informationsvisualisierung
 *
 * Berechnet die aktuelle Zoom-Stufe aus der Kamera-Distanz zur
 * Bounding-Box-Mitte und stellt Layer-Visibility-Konfigurationen bereit.
 *
 * 4 Stufen (analog zu Kartensoftware):
 *   OVERVIEW  (> 80m)  — Übersicht: Cluster, Gebäudeumriss
 *   CONTEXT   (20–80m) — Kontext: Einzelmarker, Geschoss-Labels
 *   DETAIL    (5–20m)  — Detail: Labels, Tooltips, Messwerte
 *   INSPECT   (< 5m)   — Inspektion: Popups, Koordinaten, Tags
 */

import { useEffect, useRef, useState, useMemo, useCallback } from 'react'

// ── Zoom-Stufen-Definition ────────────────────────────────────────────────

export type ZoomTier = 'overview' | 'context' | 'detail' | 'inspect'

export const ZOOM_TIERS = {
  OVERVIEW: { id: 'overview' as const, label: 'Übersicht',  minDist: 80,  maxDist: Infinity },
  CONTEXT:  { id: 'context'  as const, label: 'Kontext',    minDist: 20,  maxDist: 80 },
  DETAIL:   { id: 'detail'   as const, label: 'Detail',     minDist: 5,   maxDist: 20 },
  INSPECT:  { id: 'inspect'  as const, label: 'Inspektion', minDist: 0,   maxDist: 5 },
} as const

// ── Layer-Visibility-Matrix ───────────────────────────────────────────────

export interface FloorLabelConfig { show: boolean; style?: string; size?: string; opacity?: number; connector?: boolean }
export interface FloorLineConfig { show: boolean; opacity?: number; lineWidth?: number; color?: string }
export interface SectionLineConfig { show: boolean; opacity?: number; lineWidth?: number; ticks?: boolean; label?: string }
export interface AnnotationConfig { show: boolean; style?: string; dotSize?: number; label?: boolean; popup?: string | false }
export interface AnnotationPopupConfig { show: boolean; variant?: string; showCoords?: boolean; showTags?: boolean }
export interface MeasurementConfig { show: boolean; lines?: boolean; labels?: boolean; handles?: boolean; crosshairs?: boolean }
export interface NavCubeConfig { show: boolean; size?: string; labels?: boolean }
export interface BoundingBoxConfig { show: boolean; opacity?: number; style?: string }
export interface CloudMetadataConfig { show: boolean; content?: string[] }

export interface LayerState {
  floorLabels: FloorLabelConfig
  floorLines: FloorLineConfig
  sectionLines: SectionLineConfig
  annotations: AnnotationConfig
  annotationPopups: AnnotationPopupConfig
  measurements: MeasurementConfig
  navCube: NavCubeConfig
  boundingBox: BoundingBoxConfig
  cloudMetadata: CloudMetadataConfig
}

const LAYER_MATRIX: Record<string, Record<ZoomTier, any>> = {
  floorLabels: {
    overview: { show: true,  style: 'pill', size: 'sm', opacity: 0.6, connector: false },
    context:  { show: true,  style: 'pill', size: 'md', opacity: 0.9, connector: true },
    detail:   { show: true,  style: 'pill', size: 'md', opacity: 1.0, connector: true },
    inspect:  { show: false },
  },
  floorLines: {
    overview: { show: true,  opacity: 0.15, lineWidth: 1,   color: '#ff8800' },
    context:  { show: true,  opacity: 0.35, lineWidth: 1,   color: '#ff8800' },
    detail:   { show: true,  opacity: 0.5,  lineWidth: 1.5, color: '#ff8800' },
    inspect:  { show: false },
  },
  sectionLines: {
    overview: { show: false },
    context:  { show: true,  opacity: 0.4, lineWidth: 1,   ticks: false, label: 'icon' },
    detail:   { show: true,  opacity: 0.7, lineWidth: 1.5, ticks: true,  label: 'full' },
    inspect:  { show: true,  opacity: 1.0, lineWidth: 2,   ticks: true,  label: 'full' },
  },
  annotations: {
    overview: { show: true,  style: 'cluster', dotSize: 6,  label: false, popup: false },
    context:  { show: true,  style: 'dot',     dotSize: 10, label: false, popup: false },
    detail:   { show: true,  style: 'dot+label', dotSize: 12, label: true,  popup: 'click' },
    inspect:  { show: true,  style: 'dot+label', dotSize: 14, label: true,  popup: 'hover' },
  },
  annotationPopups: {
    overview: { show: false },
    context:  { show: false },
    detail:   { show: true,  variant: 'compact',  showCoords: false, showTags: false },
    inspect:  { show: true,  variant: 'expanded', showCoords: true,  showTags: true },
  },
  measurements: {
    overview: { show: false },
    context:  { show: true,  lines: true,  labels: false, handles: false, crosshairs: false },
    detail:   { show: true,  lines: true,  labels: true,  handles: false, crosshairs: true },
    inspect:  { show: true,  lines: true,  labels: true,  handles: true,  crosshairs: true },
  },
  navCube: {
    overview: { show: true,  size: 'large',  labels: true },
    context:  { show: true,  size: 'medium', labels: true },
    detail:   { show: true,  size: 'small',  labels: false },
    inspect:  { show: false },
  },
  boundingBox: {
    overview: { show: true,  opacity: 0.15, style: 'wireframe' },
    context:  { show: true,  opacity: 0.08, style: 'edges-only' },
    detail:   { show: false },
    inspect:  { show: false },
  },
  cloudMetadata: {
    overview: { show: true,  content: ['name', 'pointCount'] },
    context:  { show: false },
    detail:   { show: false },
    inspect:  { show: true,  content: ['name', 'pointCount', 'density', 'bounds', 'format'] },
  },
}

// ── Transition-Konfiguration ──────────────────────────────────────────────

const TRANSITION_CONFIG = {
  fadePercent: 0.15,   // 15% der Stufen-Range als Übergangszone
  durationMs: 150,
}

// ── Tier-Berechnung ───────────────────────────────────────────────────────

function getTierFromDistance(distance: number): ZoomTier {
  if (distance >= ZOOM_TIERS.OVERVIEW.minDist) return 'overview'
  if (distance >= ZOOM_TIERS.CONTEXT.minDist) return 'context'
  if (distance >= ZOOM_TIERS.DETAIL.minDist) return 'detail'
  return 'inspect'
}

function computeFadeFactors(distance: number, tier: ZoomTier): { fadeIn: number; fadeOut: number } {
  const tiers = Object.values(ZOOM_TIERS)
  const currentTier = tiers.find(t => t.id === tier)
  if (!currentTier) return { fadeIn: 1, fadeOut: 1 }

  const maxD = currentTier.maxDist === Infinity ? 200 : currentTier.maxDist
  const range = maxD - currentTier.minDist
  const fadeZone = range * TRANSITION_CONFIG.fadePercent
  const distFromMin = distance - currentTier.minDist
  const distFromMax = maxD - distance

  return {
    fadeIn: Math.min(1, fadeZone > 0 ? distFromMin / fadeZone : 1),
    fadeOut: Math.min(1, fadeZone > 0 ? distFromMax / fadeZone : 1),
  }
}

// ── Hook ──────────────────────────────────────────────────────────────────

export interface SemanticZoomState {
  tier: ZoomTier
  distance: number
  layerState: LayerState
  fadeFactors: { fadeIn: number; fadeOut: number }
}

interface UseSemanticZoomOptions {
  viewerRef: React.RefObject<any>
}

export function useSemanticZoom({ viewerRef }: UseSemanticZoomOptions): SemanticZoomState {
  const [distance, setDistance] = useState(50)
  const [tier, setTier] = useState<ZoomTier>('context')
  const distRef = useRef(50)

  // rAF-Loop: Kamera-Distanz zur Bounding-Box-Mitte berechnen
  useEffect(() => {
    let running = true
    let lastTier: ZoomTier = 'context'
    let frameCount = 0

    function loop() {
      if (!running) return
      frameCount++

      const viewer = viewerRef.current
      const THREE = (window as any).Potree?.THREE
      if (!viewer || !THREE || !viewer.scene?.getActiveCamera) {
        requestAnimationFrame(loop)
        return
      }

      const camera = viewer.scene.getActiveCamera()
      if (!camera) {
        requestAnimationFrame(loop)
        return
      }

      // Distanz-Berechnung:
      // 1. Potree view.radius (Orbit-Distanz) — der zuverlässigste Wert
      // 2. Fallback: Kamera-Position → BB-Mitte
      let d = 0

      // Methode 1: Potree Orbit-Radius
      const viewRadius = viewer.scene.view?.radius
      if (typeof viewRadius === 'number' && viewRadius > 0) {
        d = viewRadius
      } else {
        // Methode 2: Kamera → Bounding-Box-Mitte
        const pcs = viewer.scene.pointclouds
        if (pcs?.length) {
          const pc = pcs[0]
          const geo = pc.pcoGeometry
          if (geo?.boundingBox && geo?.offset) {
            const bb = geo.boundingBox
            const off = geo.offset
            const center = new THREE.Vector3(
              (bb.min.x + bb.max.x) / 2 + off.x,
              (bb.min.y + bb.max.y) / 2 + off.y,
              (bb.min.z + bb.max.z) / 2 + off.z,
            )
            d = camera.position.distanceTo(center)
          }
        }
      }

      if (d <= 0) {
        requestAnimationFrame(loop)
        return
      }

      distRef.current = d

      const newTier = getTierFromDistance(d)

      // Nur State-Update auslösen wenn sich die Stufe ändert, oder
      // alle 15 Frames für Distanz-Updates (sanfte Übergänge)
      if (newTier !== lastTier || frameCount % 15 === 0) {
        lastTier = newTier
        setDistance(d)
        setTier(newTier)

        // Custom Event für script.js dispatchen (LOD der Vanilla-Elemente)
        window.dispatchEvent(new CustomEvent('potree:semanticZoom', {
          detail: { tier: newTier, distance: d },
        }))
      }

      requestAnimationFrame(loop)
    }

    requestAnimationFrame(loop)
    return () => { running = false }
  }, [viewerRef])

  // Layer-State aus der Matrix ableiten
  const layerState = useMemo<LayerState>(() => {
    const state: any = {}
    for (const [layerName, matrix] of Object.entries(LAYER_MATRIX)) {
      state[layerName] = matrix[tier] || { show: false }
    }
    return state as LayerState
  }, [tier])

  const fadeFactors = useMemo(
    () => computeFadeFactors(distance, tier),
    [distance, tier]
  )

  return { tier, distance, layerState, fadeFactors }
}

// ── Utility: Screen-Space-Projektion ──────────────────────────────────────

export function projectToScreen(
  position: { x: number; y: number; z: number },
  camera: any,
  width: number,
  height: number,
  THREE: any,
): { x: number; y: number; behind: boolean } {
  const vec = new THREE.Vector3(position.x, position.y, position.z)
  vec.project(camera)
  return {
    x: ((vec.x + 1) / 2) * width,
    y: ((-vec.y + 1) / 2) * height,
    behind: vec.z > 1,
  }
}
